import type {CurriedSelector, Reducer, Selector} from '@/redux/types';
import type {
  FetchHotelsError,
  FetchHotelsPending,
  FetchHotelsSuccess,
} from '@/redux/v1/hotels/list/actionTypes';
import type {Hotel, Meta, State} from '@/redux/v1/hotels/list/models';
import type {CrossBordersData, Region} from '@/redux/v1/models';
import type {ActionHandlers} from '@travel-frontend/state-handling-sdk';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PENDING,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {curry, keyBy, uniq} from 'lodash';
import {createSelector} from 'reselect';

import {listState} from '@/redux/v1/hotels/hotelsState';
import {
  FETCH_ALL_HOTELS,
  FETCH_HOTELS,
  FETCH_NEXT_HOTELS,
} from '@/redux/v1/hotels/list/actionTypes';

const EMPTY_OBJECT = {};
const EMPTY_ARRAY: Array<any> = [];

export const initialState: Partial<State> = {
  id: '',
  paramHash: '',
  loading: false,
  complete: false,
  page: 1,
  pageSize: 25,
  nextPageLoading: false,
  duplicateCount: {
    before: 0,
    now: 0,
  },
};

// @ts-expect-error ...
const ignoreStaleResponses = (actionHandler) => (state, action) =>
  action.meta.requestId === state.lastRequest
    ? actionHandler(state, action)
    : state;

// @ts-expect-error ...
const actionCreators: ActionHandlers<State> = {
  [PENDING(FETCH_HOTELS)]: (state: State, action: FetchHotelsPending) => ({
    ...state,
    loading: true,
    lastRequest: action.meta.requestId,
    duplicateCount: {
      before: 0,
      now: 0,
    },
  }),
  [SUCCESS(FETCH_HOTELS)]: ignoreStaleResponses(
    (state: State, action: FetchHotelsSuccess) => ({
      ...state,
      id: action.data.id,
      paramHash:
        state.id === action.data.id ? state.paramHash : action.data.paramHash,
      loading: false,
      error: null,
      page: 1,
      // @ts-expect-error ...
      complete: action.meta.initial ? false : state.complete,
      byId: keyBy(action.data.hotels, (hotel: Hotel) => hotel.id),
      ids: action.data.hotels.map((hotel: Hotel) => hotel.id),
      metaUnfiltered: action.data.metaUnfiltered,
      metaFiltered: action.data.metaFiltered,
      translations: action.data.translations,
      crossBorders: action.data.crossBorders,
    })
  ),
  [ERROR(FETCH_HOTELS)]: ignoreStaleResponses(
    (state: State, action: FetchHotelsError) => ({
      ...state,
      loading: false,
      page: 1,
      error: action.error,
    })
  ),
  [PENDING(FETCH_ALL_HOTELS)]: (state: State) => ({
    ...state,
    loading: true,
  }),
  [SUCCESS(FETCH_ALL_HOTELS)]: (state: State, action: FetchHotelsSuccess) => ({
    ...state,
    loading: false,
    complete: true,
    error: null,
    byId: keyBy(action.data.hotels, (hotel: Hotel) => hotel.id),
    ids: action.data.hotels.map((hotel: Hotel) => hotel.id),
    // metaUnfiltered: action.data.metaUnfiltered,
    // metaFiltered: action.data.metaFiltered,
    // translations: action.data.translations,
  }),
  [ERROR(FETCH_ALL_HOTELS)]: (state: State, action: FetchHotelsError) => ({
    ...state,
    loading: false,
    error: action.error,
  }),
  [PENDING(FETCH_NEXT_HOTELS)]: (state: State) => ({
    ...state,
    nextPageLoading: true,
  }),
  [SUCCESS(FETCH_NEXT_HOTELS)]: (state: State, action: FetchHotelsSuccess) => {
    let duplicateCount = 0;
    const byId = {...state.byId};
    const newById = keyBy(action.data.hotels, (hotel: Hotel) => hotel.id);

    for (const id in newById) {
      if (byId[id]) {
        duplicateCount += 1;
      } else {
        byId[id] = newById[id];
      }
    }

    return {
      ...state,
      error: null,
      page: state.page + 1,
      nextPageLoading: false,
      byId,
      // BFF can sometimes return hotels from the previous "page",
      // make sure we don't add duplicates.
      ids: uniq([
        ...(state.ids || []),
        ...action.data.hotels.map((hotel: Hotel) => hotel.id),
      ]),
      duplicateCount: {
        before: state.duplicateCount.now,
        now: duplicateCount + state.duplicateCount.now,
      },
    };
  },
  [ERROR(FETCH_NEXT_HOTELS)]: (state: State, action: FetchHotelsError) => ({
    ...state,
    nextPageLoading: false,
    error: action.error,
  }),
  PAGE_CHANGED: (state: State) => ({
    ...state,
    page: state.page + 1,
  }),
};

export const listReducer: Reducer<State> =
  // @ts-expect-error ...
  createReducerUsingActionHandlers<State>(initialState, actionCreators);

export const selectAreHotelsLoading: Selector<boolean> = createSelector(
  listState,
  (state: State) => state.loading === true
);

export const areHotelsLoaded: Selector<boolean> = createSelector(
  [listState],
  (state: State) => state.byId !== undefined
);

export const haveHotelsFailedLoading: Selector<boolean> = createSelector(
  [listState],
  (state: State) => Boolean(state.error)
);

export const selectAreAllHotelsLoaded: Selector<boolean> = createSelector(
  [listState],
  (state: State) => state.complete === true
);

export const isNextPageLoading: Selector<boolean> = createSelector(
  [listState],
  (state: State) => state.nextPageLoading === true
);

export const getDuplicateCount: Selector<State['duplicateCount']> =
  createSelector([listState], (state: State) => state.duplicateCount);

const selectHotelsById: Selector<Record<string, Hotel>> = (state) =>
  listState(state)?.byId || {};

const selectAllHotelIds: Selector<Array<string>> = (state) =>
  listState(state).ids || EMPTY_ARRAY;

export const selectHotelById: CurriedSelector<
  string,
  null | undefined | Hotel
> = curry(
  // @ts-expect-error ...
  (hotelId, state) => selectHotelsById(state)[hotelId]
);

export const selectPage: Selector<number> = (state) => listState(state).page;

export const selectPageSize: Selector<number> = (state) =>
  listState(state).pageSize;

export const selectAllHotels: Selector<Array<Hotel>> = createSelector(
  [selectAllHotelIds, selectHotelsById],
  (allHotelIds, hotelsById) => allHotelIds.map((hotelId) => hotelsById[hotelId])
);

export const selectPaginatedHotels: Selector<Array<Hotel>> = createSelector(
  [selectPage, selectPageSize, selectAllHotelIds, selectHotelsById],
  (page, pageSize, allHotelIds, hotelsById) =>
    allHotelIds.slice(0, page * pageSize).map((hotelId) => hotelsById[hotelId])
);

export const getMetaUnfiltered: Selector<Meta> = createSelector(
  [listState],
  (state: State) =>
    state.metaUnfiltered === undefined ? EMPTY_OBJECT : state.metaUnfiltered
);

export const getMetaFiltered: Selector<Meta> = createSelector(
  [listState, areHotelsLoaded],
  (state: State) =>
    state.metaFiltered === undefined ? EMPTY_OBJECT : state.metaFiltered
);

export const getHotelCountUnfiltered: Selector<number> = createSelector(
  [getMetaUnfiltered],
  (metaUnfiltered: Meta) => metaUnfiltered.totalCount || 0
);

export const getHotelCountFiltered: Selector<number> = createSelector(
  [getMetaFiltered],
  (metaFiltered: Meta) => metaFiltered.totalCount || 0
);

export const getRegions: Selector<Array<Region>> = createSelector(
  [getMetaFiltered],
  (metaFiltered: Meta) => metaFiltered.regions || []
);

export const getCrossBordersData: Selector<CrossBordersData | undefined> = (
  state
) => listState(state).crossBorders;

export const selectTranslations: Selector<any> = (state) =>
  listState(state).translations;
